import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Citation, type Project, type TaskTemplate } from "../api/client";
import { postSseStream, type PhaseName } from "../api/sse";
import ChatStream from "../components/ChatStream";
import { useDraft } from "../hooks/useDraft";
import { getCurrentProject } from "../hooks/useCurrentProject";
import { profileForStudy } from "../data/studyFixtures";

interface Phase { name: PhaseName; count?: number }

function phaseLabel(p: Phase): string {
  switch (p.name) {
    case "retrieving":
      return "Searching the library…";
    case "retrieved":
      return p.count
        ? `Reading ${p.count} source${p.count === 1 ? "" : "s"}…`
        : "Reading…";
    case "generating":
      return "Drafting response…";
  }
}

export default function TaskPage() {
  const { templateKey } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [inputs, setInputs] = useDraft<Record<string, string>>(
    `abby_draft:task:${templateKey ?? "_index"}`,
    {},
  );
  const [streaming, setStreaming] = useState(false);
  const [output, setOutput] = useState("");
  const [citations, setCitations] = useState<Citation[]>([]);
  const [targetStudy, setTargetStudy] = useState<Project | null>(null);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedDocId, setSavedDocId] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const template = templates.find((t) => t.key === templateKey);

  useEffect(() => {
    api.listTaskTemplates().then(setTemplates).catch((e) => setError(String(e)));
    const cp = getCurrentProject();
    if (cp.kind === "project") {
      api
        .listProjects(true)
        .then((projects) => setTargetStudy(projects.find((p) => p.id === cp.id) ?? null))
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!template) return;
    setInputs((current) => {
      // If a persisted draft exists for this template, keep it. Only apply template
      // defaults when the form is genuinely empty.
      const hasAnyDraft = template.inputs.some(
        (f) => current[f.name] != null && current[f.name] !== "",
      );
      if (hasAnyDraft) return current;
      const init: Record<string, string> = {};
      for (const f of template.inputs) init[f.name] = (f as { default?: string }).default ?? "";
      return init;
    });
    setOutput("");
    setCitations([]);
    setSavedDocId(null);
    setError(null);
  }, [template?.key]);

  if (!templateKey) {
    return (
      <>
        <div className="main-header">
          <div>
            <span className="crumb">{targetStudy ? `/ studies / ${targetStudy.name} / tasks` : "/ tasks"}</span>
            <h2>Tasks</h2>
          </div>
        </div>
        <div className="main-body">
          {targetStudy && (
            <div className="research-target task-target">
              <span className="project-dot" style={{ background: targetStudy.color || "var(--accent)" }} />
              <span className="lab">task target</span>
              <strong>{targetStudy.name}</strong>
              <span>{profileForStudy(targetStudy).phase}</span>
            </div>
          )}
          <div className="task-grid">
            {templates.map((t) => (
              <div key={t.key} className="task-card" onClick={() => navigate(`/tasks/${t.key}`)}>
                <h3>{t.display_name}</h3>
                <p>{t.short_description}</p>
              </div>
            ))}
          </div>
          {error && <p className="error">{error}</p>}
        </div>
      </>
    );
  }

  if (!template) {
    return (
      <>
        <div className="main-header">
          <h2>Task</h2>
        </div>
        <div className="main-body">
          <p className="muted">Loading…</p>
        </div>
      </>
    );
  }

  async function run() {
    if (!template || streaming) return;
    setStreaming(true);
    setOutput("");
    setCitations([]);
    setSavedDocId(null);
    setPhase(null);
    setError(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      for await (const evt of postSseStream(
        `/api/tasks/${template.key}/run`,
        { inputs },
        ctrl.signal,
      )) {
        if (evt.type === "phase") setPhase({ name: evt.name, count: evt.count });
        else if (evt.type === "retrieved") setCitations(evt.citations);
        else if (evt.type === "token") {
          setPhase(null);
          setOutput((o) => o + evt.text);
        }
        else if (evt.type === "error") setError(evt.detail);
        else if (evt.type === "done") break;
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStreaming(false);
      setPhase(null);
      abortRef.current = null;
    }
  }

  async function saveAsDoc() {
    if (!template || !output) return;
    const title = `${template.display_name} — ${new Date().toLocaleString()}`;
    const doc = await api.saveDoc({
      title,
      body_md: output,
      created_from: { kind: "task_run", template: template.key, inputs },
    });
    setSavedDocId(doc.id);
  }

  return (
    <>
      <div className="main-header">
        <div>
          <span className="crumb">{targetStudy ? `/ studies / ${targetStudy.name} / task` : "/ task"}</span>
          <h2>{template.display_name}</h2>
        </div>
        <span className="spacer" />
        <button className="ghost" onClick={() => navigate("/tasks")}>← All tasks</button>
      </div>
      <div className="main-body">
        {targetStudy && (
          <div className="research-target task-target">
            <span className="project-dot" style={{ background: targetStudy.color || "var(--accent)" }} />
            <span className="lab">task target</span>
            <strong>{targetStudy.name}</strong>
            <span>{profileForStudy(targetStudy).phase}</span>
          </div>
        )}
        <div className="task-form">
          {template.inputs.map((f) => (
            <div key={f.name}>
              <label>{f.label}{(f as { optional?: boolean }).optional && <span className="muted"> (optional)</span>}</label>
              {f.type === "select" ? (
                <select value={inputs[f.name] ?? ""} onChange={(e) => setInputs((s) => ({ ...s, [f.name]: e.target.value }))}>
                  {(f.options ?? []).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={inputs[f.name] ?? ""} onChange={(e) => setInputs((s) => ({ ...s, [f.name]: e.target.value }))} rows={f.name === "draft_text" ? 10 : 4} />
              ) : (
                <input value={inputs[f.name] ?? ""} onChange={(e) => setInputs((s) => ({ ...s, [f.name]: e.target.value }))} />
              )}
            </div>
          ))}
          <div className="row">
            <button className="primary" onClick={run} disabled={streaming}>
              {streaming
                ? <><span className="spinner" />{phase ? phaseLabel(phase) : "Working…"}</>
                : "Run"}
            </button>
            {streaming && <button className="ghost" onClick={() => abortRef.current?.abort()}>Stop</button>}
          </div>
        </div>

        {(output || streaming) && (
          <>
            {citations.length > 0 && (
              <div className="retrieved-banner" style={{ marginTop: 16, maxWidth: 900 }}>
                <span className="label">Retrieved:</span>
                {citations.slice(0, 10).map((c, i) => (
                  <span key={`${c.source_id}-${i}`} className="tag">{c.title}{c.year ? ` (${c.year})` : ""}</span>
                ))}
              </div>
            )}
            <div className="task-result">
              <ChatStream content={output} citations={citations} />
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button onClick={saveAsDoc} disabled={!output || streaming || savedDocId !== null}>
                {savedDocId ? "Saved ✓" : "Save as Doc"}
              </button>
            </div>
          </>
        )}

        {error && <p className="error" style={{ marginTop: 12 }}>{error}</p>}
      </div>
    </>
  );
}
